import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { ClockCountdownIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import Icon from '../Icons'
import { formatMoney } from '../../helpers/helpers'
import { setBuildingData } from '../../reducers/parkingReducer'
import API from '../../api/api'

import { getParkingTime, getPayedByResident, getPaidByResidentLabel } from './helpers'
import ParkingInfoSection from './components/ParkingInfoSection'

const ParkingServicesSummary = ({ ticketSummary, withTip, buildingData, setBuildingDataStore }) => {
  const { t } = useTranslation(['parking'])
  const [cost, setCots] = useState(0)

  useEffect(() => {
    ;(async () => {
      if (ticketSummary.buildingId) {
        try {
          const resp = await API.getSingleBuilding(ticketSummary.buildingId)
          setBuildingDataStore(resp)
        } catch (error) {
          console.error(error)
        }
      }
    })()
  }, [ticketSummary.buildingId])

  useEffect(() => {
    ;(async () => {
      if (ticketSummary.cost === 0 && ticketSummary.ticketNumber) {
        try {
          const resp = await API.calculateParkingFee(ticketSummary.ticketNumber)
          setCots(resp.data.serviceFee)
        } catch (error) {
          console.error(error)
        }
      }
    })()
  }, [ticketSummary.cost, ticketSummary.ticketNumber])

  const residentPayment = getPayedByResident(ticketSummary, cost, ticketSummary.paidByResident)

  const renderCostDetails = () => {
    if (withTip) {
      return (
        <div className="parking-info__section-row">
          {ticketSummary.clientName && residentPayment > 0 ? (
            <div className="parking-info__section-info">
              <div className="parking-info__section-info-key">
                {getPaidByResidentLabel(ticketSummary)}
              </div>
              <div className="parking-info__section-info-value">{formatMoney(residentPayment)}</div>
            </div>
          ) : null}
          <div className="parking-info__section-info">
            <div className="parking-info__section-info-key">
              <Icon icon="moneyBag" /> {t('parking:servicesSummary.totalServiceCost')}
            </div>
            <div className="parking-info__section-info-value">
              {formatMoney(((cost || ticketSummary.cost) + ticketSummary.amountPaid) / 100)}
            </div>
          </div>
          <div className="parking-info__section-info">
            <div className="parking-info__section-info-key">
              <Icon icon="moneyBag" /> {t('parking:paymentForm.tipLabel')}
            </div>
            <div className="parking-info__section-info-value">
              {ticketSummary.tip ? formatMoney(ticketSummary.tip / 100) : '$0.00'}
            </div>
          </div>
        </div>
      )
    } else if (cost) {
      return (
        <>
          {ticketSummary.clientName && residentPayment > 0 ? (
            <div className="parking-info__section-info-full-row">
              <div className="parking-info__section-info-key">
                {getPaidByResidentLabel(ticketSummary)}
              </div>
              <div className="parking-info__section-info-value">{formatMoney(residentPayment)}</div>
            </div>
          ) : null}
          <div className="parking-info__section-info-full-row">
            <div className="parking-info__section-info-key">
              <Icon icon="moneyBag" /> {t('parking:servicesSummary.totalServiceCost')}
            </div>
            <div className="parking-info__section-info-value">
              {formatMoney(((cost || ticketSummary.cost) + ticketSummary.amountPaid) / 100)}
            </div>
          </div>
        </>
      )
    } else {
      return null
    }
  }

  const timeStart = toZonedTime(new Date(ticketSummary.startTime), buildingData.timezone)
  const timeEnd = toZonedTime(new Date(ticketSummary.endTime), buildingData.timezone)

  return (
    <ParkingInfoSection title={t('parking:servicesSummary.title')}>
      <div className="parking-info__section-row">
        <div className="parking-info__section-info">
          <div className="parking-info__section-info-key">
            <Icon icon="timer" /> {t('parking:servicesShortInfo.startTime')}
          </div>
          <div className="parking-info__section-info-value">
            {ticketSummary.startTime
              ? format(timeStart, 'hh:mm a')
              : t('parking:servicesSummary.na')}
          </div>
          <div style={{ fontSize: '14px' }}>
            {ticketSummary.startTime ? format(timeStart, 'MMM do') : ''}
          </div>
        </div>
        <div className="parking-info__section-info">
          <div className="parking-info__section-info-key">
            <Icon icon="timer" /> {t('parking:servicesSummary.endTime')}
          </div>
          <div className="parking-info__section-info-value">
            {ticketSummary.endTime ? format(timeEnd, 'hh:mm a') : t('parking:servicesSummary.na')}
          </div>
          <div style={{ fontSize: '14px' }}>
            {ticketSummary.endTime ? format(timeEnd, 'MMM do') : ''}
          </div>
        </div>
      </div>

      <div className="parking-info__section-info-full-row" style={{ marginBottom: '8px' }}>
        <div className="parking-info__section-info-key">
          <ClockCountdownIcon /> {t('parking:servicesSummary.totalParkingTime')}
        </div>
        <div className="parking-info__section-info-value">
          {ticketSummary.startTime
            ? getParkingTime(
                t,
                ticketSummary.startTime,
                ticketSummary.endTime || toZonedTime(new Date(), buildingData.timezone),
              )
            : t('parking:servicesSummary.na')}
        </div>
      </div>

      {renderCostDetails()}

      <div className="parking-info__section-info-full-row">
        <div className="parking-info__section-info-key">
          <Icon icon="tagIcon" /> {t('parking:servicesShortInfo.ticketNo')}
        </div>
        <div className="parking-info__section-info-value">{ticketSummary.ticketNumber}</div>
      </div>
    </ParkingInfoSection>
  )
}

ParkingServicesSummary.propTypes = {
  ticketSummary: PropTypes.object,
  withTip: PropTypes.bool,
  buildingData: PropTypes.object,
  setBuildingDataStore: PropTypes.func,
}

export default connect((state) => ({ buildingData: state.parking.buildingData }), {
  setBuildingDataStore: setBuildingData,
})(ParkingServicesSummary)
